import { SiteUrls } from '@tableau/shared-urls';
import { IUserNotification, IUserNotificationContentProvider, NotificationContentProviders,UserNotificationType } from '@tableau/user-notifications-client-core';
import { TabActivationMode, TabAndPanelContents, TabListAlignment, TabsWidget } from '@tableau/widgets';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import _ from 'underscore';

import { errorBoundary, sendTelemetryErrorEvent } from '~src/common/react/components/ErrorBoundary';

import { getAppStore } from '../../app/AppStore';
import { Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { imgPath } from '../../common/utilities/ImgUrl';
import { Keys } from '../../ts/helpers/Keys';
import { IUserNotificationMenuItemProvider } from '../menuProviders/IUserNotificationMenuItemProvider';
import { NotificationMenuitemProviders } from '../menuProviders/NotificationMenuProviders';
import { UserNotificationActions } from '../redux/UserNotificationActions';
import { NotificationCenterStyles } from '../styles/notificationCenterStyles';
import * as notificationStyles from '../styles/notificationStyles';
import { sendUserNotificationsCenterPerfTelemetry, sendUserNotificationsPanelTelemetry, UserNotificationsTelemetryEvents } from '../telemetry/UserNotificationsTelemetry';
import { getEarlyNotificationTime } from '../utility/notificationHelper';
import { isNullOrUndefined } from '../utility/notificationHelper';
import { Notification } from './Notification';
import { NotificationLoader } from './NotificationLoader';
import { NotificationLoadFail } from './NotificationLoadFail';
import { NotificationPanelHeader } from './NotificationPanelHeader';

//This state is used to control when to show the loading and failure state
export enum NotificationDisplayMode {
  NotificationLoaded,
  NotificationLoading,
  NotificationLoadFailed
}

export type NotificationPanelProps = {
  siteUrls: SiteUrls;
  userNotificationActions: UserNotificationActions;
  displayMode: NotificationDisplayMode;
  closePanel: () => void;
  domainName: string;
  userName: string;
  userId: string;
  siteLuid: string;
};

export type NotificationPanelState = {
  nextPageToken: string;
  notifications: Array<IUserNotification<any>>;
  currentTabIndex: number;
  markAllAsReadClickTime: number;
  unreadCount: number;
};

export enum NotificationStatus {
  READ,
  UNREAD,
  ALL
}

enum NotificationSection {
  Earlier = 'earlier',
  New = 'new'
}

export const pageSize = 5;

export class NotificationPanel extends React.PureComponent<NotificationPanelProps, NotificationPanelState> {

  private unsubscribeFromAppStore: () => void;
  private notificationContentProviders: _.Dictionary<IUserNotificationContentProvider<any>>;
  private notificationMenuItemProviders: _.Dictionary<IUserNotificationMenuItemProvider<any>>;
  private tabsDimensionOverrides = {
    tabHeight: 32,
    tabPanelPadding: '0'
  };
  private loadStartTime: number;

  constructor(props: NotificationPanelProps) {
    super(props);
    this.notificationContentProviders = NotificationContentProviders();
    this.notificationMenuItemProviders = NotificationMenuitemProviders(this.props.userNotificationActions);
    this.state = {
      nextPageToken: null,
      notifications: [],
      currentTabIndex: 0,
      markAllAsReadClickTime: 0,
      unreadCount: 0
    };
    this.renderNotifications = this.renderNotifications.bind(this);
    this.onAppStoreChange = this.onAppStoreChange.bind(this);
    this.onTabChange = this.onTabChange.bind(this);
    this.getNotificationsForCurrentTab = this.getNotificationsForCurrentTab.bind(this);
    this.loadStartTime = performance.now();
  }

  public componentDidMount() {
    this.unsubscribeFromAppStore = getAppStore().subscribe(this.onAppStoreChange);
  }

  public componentWillUnmount() {
    this.unsubscribeFromAppStore();
  }

  public componentDidUpdate(prevProps: NotificationPanelProps, prevState: NotificationPanelState) {
    //record panel load time if the notification center is opened or if the notification panel refreshes with new notifications
    if(this.state.notifications.length > prevState.notifications.length) {
      const loadTime = performance.now() - this.loadStartTime;
      sendUserNotificationsCenterPerfTelemetry(UserNotificationsTelemetryEvents.notificationCenterPerfLoadTime,
        this.props.userId, this.props.siteLuid, loadTime, this.state.notifications.length);
    }

    //record panel load time if mark all as read is clicked and there were multiple unread notifications 
    if(prevState.unreadCount > 1 && this.state.unreadCount === 0 && this.state.markAllAsReadClickTime > 0) {
      const loadTime = performance.now() - this.state.markAllAsReadClickTime;
      sendUserNotificationsCenterPerfTelemetry(UserNotificationsTelemetryEvents.markAllAsReadPerfLoadTime,
        this.props.userId, this.props.siteLuid, loadTime, this.state.notifications.length);
    }
  }

  private onAppStoreChange() {
    const { notifications, nextPageToken } = getAppStore().getState().notificationState;
    if (notifications !== null) {
      let notificationValues = _.values(notifications);
      const newUnreadCount = notificationValues.filter((n) => !n.read).length;
      if (notificationValues.length !== this.state.notifications.length || newUnreadCount !== this.state.unreadCount) {
        this.setState({
          nextPageToken: nextPageToken,
          notifications: notificationValues,
          unreadCount: newUnreadCount,
        });
      }
    }
  }

  private getNotificationsForCurrentTab() {
    return this.state.currentTabIndex === 0 ? this.state.notifications : _.filter(this.state.notifications, (value: IUserNotification<any>) => !value.read);
  }

  private onTabChange(tabIndex: number): void {
    sendUserNotificationsPanelTelemetry(UserNotificationsTelemetryEvents.changeTabs, this.props.userId, this.props.siteLuid, tabIndex);
    this.setState({ currentTabIndex: tabIndex });
  }

  private updateMarkAllAsReadClickTime(currentTime: number): void {
    this.setState({ markAllAsReadClickTime: currentTime});
  }

  private loadMore() {
    this.props.userNotificationActions.getUserNotifications(this.state.nextPageToken);
  }

  public render() {
    const userSettingUrl = this.props.siteUrls.userSettingsUrl(this.props.userName, this.props.domainName);
    return <div id={'notification-panel'} role={'dialog'} data-tb-test-id='notification-panel' style={NotificationCenterStyles.notificationContainerStyle}>
      <NotificationPanelHeader
        userPreferenceUrl={userSettingUrl}
        isMarkAllReadDisabled={!this.state.notifications || this.state.notifications.length < 1}
        userNotificationActions={this.props.userNotificationActions}
        userNotifications={_.indexBy(this.state.notifications, 'id')}
        displayMode={this.props.displayMode}
        closePanel={this.props.closePanel}
        userId={this.props.userId}
        siteLuid={this.props.siteLuid}
        updateMarkAllAsReadClickTime={this.updateMarkAllAsReadClickTime.bind(this)} />
      {this.props.displayMode === NotificationDisplayMode.NotificationLoading ? <NotificationLoader /> :
        <div className={NotificationCenterStyles.notificationPanelTab}>
          <TabsWidget
            activationMode={TabActivationMode.Manual}
            anchorTabIndex={this.state.currentTabIndex}
            ariaLabel={Messages.userNotificationTabLabel()}
            initialActiveIndex={0}
            tabListAlignment={TabListAlignment.Left}
            tabs={this.getNotificationTabs()}
            dimensionOverrides={this.tabsDimensionOverrides}
            onActiveIndexChanged={this.onTabChange}
            testId={'notification-center-tab'}
          />
        </div>
      }
    </div>;
  }

  private renderEmptyNotification(tabState: NotificationStatus) {
    return (
      <div data-tb-test-id='empty-notification-panel' style={NotificationCenterStyles.notificationEmptyList}>
        <div> <img src={imgPath('notification_empty_bell.svg')} alt={Messages.userNotificationBellIconAlt()} height="80" /></div>
        <div style={NotificationCenterStyles.notificationEmptyTitle}>{Messages.userNotificationEmptyNotification()}</div>
        {
          tabState === NotificationStatus.ALL && <div style={NotificationCenterStyles.notificationEmptySubtitle}>{Messages.userNotificationEmptyNotificationMessage()}</div>
        }
      </div>);
  }

  private getNotificationTabs(): TabAndPanelContents[] {
    return [{
      panelContent: this.renderNotifications(NotificationStatus.ALL),
      tabContent: { label: Messages.userNotificationAllTabTitle(), title: Messages.userNotificationAllNotifications() }
    }, {
      panelContent: this.renderNotifications(NotificationStatus.UNREAD),
      tabContent: { label: Messages.userNotificationUnreadTabTitle(), title: Messages.userNotificationUnreadNotifications() }
    }];
  }

  private renderNotifications(tabState: NotificationStatus): JSX.Element {
    let notifications = this.getNotificationsForCurrentTab();
    if (this.props.displayMode === NotificationDisplayMode.NotificationLoadFailed) {
      return <NotificationLoadFail />;
    }

    if (!notifications || notifications.length === 0) {
      return this.renderEmptyNotification(tabState);
    }

    let earlierTime = getEarlyNotificationTime();
    const groupedNotifications = _.partition(notifications, (value: IUserNotification<any>) => value.creationTime >= earlierTime);

    return (

      <div style={NotificationCenterStyles.notificationTabContent} data-tb-test-id='notifications-list-container'>
        <InfiniteScroll
          pageStart={0}
          loadMore={this.loadMore.bind(this)}
          initialLoad={true}
          hasMore={this.state.nextPageToken != null}
          useWindow={false}
          isReverse={false}
          threshold={100}
        >
          {this.renderNotificationSection(NotificationSection.New, tabState, groupedNotifications[0])}
          {this.renderNotificationSection(NotificationSection.Earlier, tabState, groupedNotifications[1])}
        </InfiniteScroll>
      </div>);
  }

  private renderNotificationSection(section: NotificationSection, tabState: NotificationStatus, notifications: Array<IUserNotification<any>>) {
    let title = section === NotificationSection.Earlier ? Messages.userNotificationEarlierSectionTitle() : Messages.userNotificationNewSectionTitle();
    return notifications.length ? (
      <>
        <div style={notificationStyles.notificationSectionTitle}>{title}</div>
        <div role='grid' className={`${section}-notifications`} data-tb-test-id={`notification-tab-${tabState}-${title}`}>
          {
            notifications.map((notification, index) => {
              if (Object.values(UserNotificationType).includes(notification.type)) {
                const notificationProvider = this.notificationContentProviders[notification.type];
                const notificationMenuItemProvider = this.notificationMenuItemProviders[notification.type];
                return <Notification
                  key={index}
                  notification={notification}
                  notificationProvider={notificationProvider}
                  notificationMenuItemProvider={notificationMenuItemProvider}
                  siteUrls={this.props.siteUrls}
                  onKeyDown={(e) => this.handleKeyDown(e, notification)}
                  onNotificationClick={() => this.onNotificationClick(notification)}
                  closePanel={this.props.closePanel}
                  userId={this.props.userId}
                  siteLuid={this.props.siteLuid}
                />;
              }
            })
          }
        </div></>) : '';
  }
  private onNotificationClick(notification: IUserNotification<any>) {
    // If the notification url is invalid, do not redirect
    if (isNullOrUndefined(notification.targetInfo?.targetUrl)) return;
    if (!notification.read) {
      this.props.userNotificationActions.updateUserNotificationsStatus([notification.id], true);
    }
    this.props.closePanel();
    Location.instance.setUrl(notification.targetInfo?.targetUrl);
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLElement>, notification: IUserNotification<any>): void => {

    if (e.keyCode === Keys.ArrowDown || e.keyCode === Keys.ArrowUp) {
      e.preventDefault();
      e.stopPropagation();
      const selector = '.' + notificationStyles.notificationItem;
      const notifications = e.currentTarget.parentElement.parentElement.parentElement.querySelectorAll<HTMLDivElement>(selector);
      let index: number = Array.prototype.indexOf.call(notifications, e.target);
      if (e.keyCode === Keys.ArrowDown) {
        index = (index === notifications.length - 1) ? 0 : index + 1;
      } else if (e.keyCode === Keys.ArrowUp) {
        index = (index === 0) ? notifications.length - 1 : index - 1;
      }
      notifications[index].focus();
    } else if (e.keyCode === Keys.ArrowRight) {
      e.preventDefault();
      e.stopPropagation();
      const actionButton = e.currentTarget.nextElementSibling.querySelector<HTMLDivElement>('button');
      actionButton?.focus();
    } else if (e.keyCode === Keys.Enter || e.keyCode === Keys.Space) {
      e.preventDefault();
      e.stopPropagation();
      this.onNotificationClick(notification);

    }
  }
}

export const UserNotificationPanel = errorBoundary<NotificationPanelProps>(sendTelemetryErrorEvent)(NotificationPanel);